import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { signUp, signIn } from '../services/authService';

const SIGNUP_COOLDOWN_MS = 10_000;
let lastSignupAttempt = 0;
const DEFAULT_OASIX_CATEGORY = 'Saas Manager';
const DEFAULT_OASIX_TAGS = ['Governance', 'Training'];

export const AdminSignupView: React.FC = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (honeypot) return;

    const now = Date.now();
    if (now - lastSignupAttempt < SIGNUP_COOLDOWN_MS) {
      setError('Please wait a few seconds before trying again.');
      return;
    }
    lastSignupAttempt = now;

    if (!companyName.trim()) { setError('Company name is required.'); return; }
    if (!name.trim()) { setError('Name is required.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must contain uppercase, lowercase, and a number.'); return;
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      setError('Password must contain at least one special character.'); return;
    }
    setLoading(true);
    try {
      const { user } = await signUp(email, password);
      if (!user) throw new Error('Account creation failed');

      const identities = (user as any).identities;
      if (Array.isArray(identities) && identities.length === 0) {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }

      const { data: company, error: companyErr } = await supabase
        .from('companies')
        .insert([{ name: companyName.trim() }])
        .select('id')
        .single();
      if (companyErr || !company) throw new Error(companyErr?.message || 'Failed to create company');

      const nameParts = name.trim().split(/\s+/).filter(Boolean);
      const firstName = nameParts[0] ?? name.trim();
      const lastName = nameParts.slice(1).join(' ');

      const { error: empErr } = await supabase.from('employees').insert([{
        name: name.trim(),
        first_name: firstName,
        last_name: lastName,
        email,
        role: 'Administrator',
        auth_role: 'admin',
        user_id: user.id,
        company_id: company.id,
      }]);
      if (empErr) throw new Error(empErr.message);

      const { error: settingsErr } = await supabase.from('app_settings').insert([{
        company_id: company.id,
        currency: 'EUR',
        currency_symbol: '€',
        renewal_warning_days: 30,
        notice_warning_days: 60,
        categories: [DEFAULT_OASIX_CATEGORY],
        tags: DEFAULT_OASIX_TAGS,
        teams: [],
      }]);
      if (settingsErr) throw new Error(settingsErr.message);

      const { data: existingOasixTool } = await supabase
        .from('tools')
        .select('id')
        .eq('company_id', company.id)
        .ilike('name', 'Oasix')
        .maybeSingle();

      if (!existingOasixTool) {
        const { error: defaultToolErr } = await supabase.from('tools').insert([{
          id: crypto.randomUUID(),
          name: 'Oasix',
          category: DEFAULT_OASIX_CATEGORY,
          importance: 'Supporting',
          pricing_model: 'Fixed',
          base_price: 0,
          user_count: 1,
          monthly_cost: 0,
          complexity: 1,
          team_literacy: 10,
          renewal_date: null,
          notice_deadline: '',
          satisfaction_score: 100,
          description: 'Core SaaS governance platform',
          website_url: 'https://oasix.io',
          notes: 'Seeded by default for every new instance.',
          assigned_user_ids: [],
          tags: DEFAULT_OASIX_TAGS,
          feedback: [],
          company_id: company.id,
        }]);
        if (defaultToolErr) throw new Error(defaultToolErr.message);
      }

      try {
        await signIn(email, password);
      } catch {
        // Auto-login failed (e.g. email confirmation required) — fallback to login page
      }
      navigate('/admin/onboarding');
    } catch (err: any) {
      setError(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Create your Oasix account</h1>
          <p className="text-slate-500 text-sm mt-1">Set up your organization and admin account.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>
          )}
          <div aria-hidden="true" className="invisible absolute -left-[9999px] h-0 overflow-hidden">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="signup-company" className="block text-sm font-medium text-slate-700 mb-1">Company name</label>
            <input
              id="signup-company"
              type="text"
              required
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder="Acme Inc."
            />
          </div>
          <div>
            <label htmlFor="signup-name" className="block text-sm font-medium text-slate-700 mb-1">Your name</label>
            <input
              id="signup-name"
              type="text"
              required
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label htmlFor="signup-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              id="signup-email"
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder="admin@company.com"
            />
          </div>
          <div>
            <label htmlFor="signup-password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input
              id="signup-password"
              type="password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder="Min 8 chars, upper + lower + number"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-2.5 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create account'}
          </button>
          <div className="text-center">
            <Link to="/login" className="text-indigo-600 hover:underline text-sm">Already have an account? Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
};
